import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext } from 'formik';
import { Form } from 'formik-antd';
import {
  Row,
  Col,
  Button,
  Spin,
  Checkbox,
  Input,
  Typography,
  theme,
} from 'antd';
import useStyles from 'components/Forms/styles';
import InputField from 'components/Forms/input';
import SelectField from 'components/Forms/select';
import CheckboxField from 'components/Forms/checkbox';
import GeolocationField from 'components/Forms/geolocation';
import { useSelector } from 'react-redux';
import { storeLoadedSelector } from 'redux/store/selectors';
import { notifierTypeGlobalsSelector } from 'redux/globals/selectors';
import { OTHER_NOTIFIER_TYPE } from 'constants/options';
import Card from 'components/card';
import { SettingOutlined } from '@ant-design/icons';
import NotificationTemplatesModal from 'components/NotificationTemplates';
import { OWNER_TYPE } from 'services/NotificationTemplatesService';

const StoreNotificationTemplates = ({ ownerId, parentName, parentOwnerId }) => {
  const [enabled, setEnabled] = useState(false);
  const [channelOpen, setChannelOpen] = useState(null);

  if (!ownerId) return null;

  return (
    <Col
      xs={24}
      sm={24}
      style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      <Checkbox
        checked={enabled}
        onChange={(e) => setEnabled(e.target.checked)}
      >
        Envío de notificaciones a usuarios
      </Checkbox>
      {enabled && (
        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            type="link"
            size="small"
            icon={<SettingOutlined />}
            style={{ paddingLeft: 0 }}
            onClick={() => setChannelOpen('email')}
          >
            Configurar plantillas de mail
          </Button>
          <Button
            type="link"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => setChannelOpen('whatsapp')}
          >
            Configurar plantillas de WhatsApp
          </Button>
        </div>
      )}
      <NotificationTemplatesModal
        open={channelOpen !== null}
        channel={channelOpen || undefined}
        onClose={() => setChannelOpen(null)}
        ownerType={OWNER_TYPE.STORE}
        ownerId={ownerId}
        ownerName="Tienda"
        parentName={parentName}
        parentOwnerId={parentOwnerId}
      />
    </Col>
  );
};

StoreNotificationTemplates.propTypes = {
  ownerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  parentName: PropTypes.string,
  parentOwnerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

StoreNotificationTemplates.defaultProps = {
  ownerId: null,
  parentName: '',
  parentOwnerId: null,
};

const Integration = ({ disabledIntegration }) => {
  const notifierTypes = useSelector(notifierTypeGlobalsSelector);
  const { values } = useFormikContext();

  const optionsNotifierType = [
    OTHER_NOTIFIER_TYPE,
    ...notifierTypes.map((notifierType) => ({
      label: notifierType.description,
      value: notifierType.value,
    })),
  ];

  const showUuid =
    values?.notifierType === OTHER_NOTIFIER_TYPE.value && values?.uuid;

  return (
    <>
      <Col xs={24} sm={24} style={{ display: 'flex', justifyContent: 'start' }}>
        <CheckboxField
          label="Integracion con canal de venta"
          fieldName="hasIntegration"
          disabled={disabledIntegration}
        />
      </Col>
      {values?.hasIntegration && (
        <>
          <Col span={showUuid ? 12 : 24}>
            <SelectField
              label="Tipo de integración"
              fieldName="notifierType"
              options={optionsNotifierType}
              disabled={disabledIntegration}
            />
          </Col>
          {showUuid && (
            <Col span={12} style={{ textAlign: 'left' }}>
              <label htmlFor="store-uuid">UUID de la tienda</label>
              <Input
                id="store-uuid"
                value={values.uuid}
                readOnly
                suffix={<Typography.Text copyable={{ text: values.uuid }} />}
              />
            </Col>
          )}
          <Col span={24} style={{ marginTop: 24 }}>
            <InputField
              type="string"
              label="Usuario"
              fieldName="username"
              disabled={disabledIntegration}
            />
          </Col>
          <Col xs={24} md={12}>
            <InputField
              label="Contraseña"
              fieldName="password"
              type="password"
              disabled={disabledIntegration}
            />
          </Col>
          <Col xs={24} md={12}>
            <InputField
              label="Confirmar Contraseña"
              fieldName="confirmpassword"
              type="password"
              disabled={disabledIntegration}
            />
          </Col>
        </>
      )}
    </>
  );
};

const SaveButton = ({ loading, className }) => {
  const { status, isValid } = useFormikContext();
  return (
    <Button
      loading={loading || status?.geolocating}
      disabled={!isValid}
      type="primary"
      htmlType="submit"
      className={className}
      size="large"
    >
      Guardar
    </Button>
  );
};

SaveButton.propTypes = {
  loading: PropTypes.bool.isRequired,
  className: PropTypes.string.isRequired,
};

const Store = ({
  validationSchema,
  onSubmit,
  initialValues,
  disabledIntegration,
  ownerId,
  parentName,
  parentOwnerId,
}) => {
  const formik = useRef(null);
  const { token } = theme.useToken();
  const classes = useStyles({ theme: token });
  const loaded = useSelector(storeLoadedSelector);

  return (
    <Spin spinning={!loaded}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        innerRef={formik}
      >
        <Form>
          <Card title="Datos de la Tienda">
            <Row align="top">
              <Col span={24} className={classes.cardContent}>
                <Row justify="center" align="stretch" gutter={[8, 8]} wrap>
                  <Col xs={24} sm={12}>
                    <InputField type="string" label="Nombre" fieldName="name" />
                  </Col>
                  <Col xs={24} sm={12}>
                    <InputField
                      type="string"
                      label="Código de tienda"
                      fieldName="id"
                      disabled
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <Row>
                      <Col xs={24}>
                        <InputField
                          type="string"
                          label="Dirección de despacho"
                          fieldName="address"
                        />
                      </Col>

                      <Col xs={24}>
                        <GeolocationField addressFieldName="address" />
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={24} sm={12}>
                    <InputField
                      type="string"
                      label="w3w"
                      fieldName="geokey"
                      disabled
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <InputField
                      type="string"
                      label="Nombre de contacto"
                      fieldName="contactName"
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <InputField label="Número de teléfono" fieldName="phone" />
                  </Col>
                  <Col xs={24} sm={12}>
                    <InputField
                      type="string"
                      label="Detalle"
                      fieldName="detail"
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <InputField type="string" label="Email" fieldName="email" />
                  </Col>
                  <Col
                    xs={24}
                    sm={24}
                    style={{ display: 'flex', justifyContent: 'start' }}
                  >
                    <CheckboxField label="Activa" fieldName="active" />
                  </Col>
                  <StoreNotificationTemplates
                    ownerId={ownerId}
                    parentName={parentName}
                    parentOwnerId={parentOwnerId}
                  />
                </Row>
              </Col>
            </Row>

            {/* Sección de Integración */}
            <Row style={{ marginTop: '24px' }}>
              <Col span={24} className={classes.cardContent}>
                <Row justify="center" align="middle" gutter={22}>
                  <Integration disabledIntegration={disabledIntegration} />
                </Row>
              </Col>
            </Row>
            <Row type="flex" justify="end">
              <Col span={24} style={{ display: 'flex', justifyContent: 'end' }}>
                <SaveButton loading={!loaded} className={classes.saveButton} />
              </Col>
            </Row>
          </Card>
        </Form>
      </Formik>
    </Spin>
  );
};
Store.propTypes = {
  validationSchema: PropTypes.shape({}),
  disabledIntegration: PropTypes.bool,
  ownerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  parentName: PropTypes.string,
  parentOwnerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
    detail: PropTypes.string,
    address: PropTypes.string,
    phone: PropTypes.string,
    contactName: PropTypes.string,
    email: PropTypes.string,
    active: PropTypes.bool,
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
};

Store.defaultProps = {
  disabledIntegration: false,
  ownerId: null,
  parentName: '',
  parentOwnerId: null,
  validationSchema: null,
  initialValues: {
    name: '',
    id: '',
    detail: '',
    address: '',
    phone: '',
    contactName: '',
    email: '',
    active: true,
    geokey: '',
    lat: null,
    lng: null,
  },
};

Integration.propTypes = {
  disabledIntegration: PropTypes.bool,
};

Integration.defaultProps = {
  disabledIntegration: false,
};

export default Store;
